import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, getProfile } from "./api";
import { Shell, Panel, BigBtn, GhostBtn, inputCls } from "./ui";

function SignaturePad({ onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const start = (e) => {
    e.preventDefault();
    canvasRef.current.setPointerCapture(e.pointerId);
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.beginPath(); ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    const ctx = canvasRef.current.getContext("2d");
    ctx.strokeStyle = "#fafafa"; ctx.lineWidth = 2.5; ctx.lineCap = "round";
    const { x, y } = pos(e);
    ctx.lineTo(x, y); ctx.stroke();
    if (!hasInk) setHasInk(true);
    onChange(canvasRef.current.toDataURL("image/png"));
  };
  const end = () => { drawing.current = false; };
  const clear = () => {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setHasInk(false);
    onChange(null);
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        data-testid="signature-canvas"
        width={480} height={170}
        className="w-full rounded-xl bg-black/50 border border-white/20"
        style={{ touchAction: "none" }}
        onPointerDown={start} onPointerMove={move} onPointerUp={end} onPointerLeave={end}
      />
      <div className="flex justify-between items-center mt-1.5">
        <p className="text-xs text-white/35">{hasInk ? "Signature captured" : "Sign above with your finger"}</p>
        <GhostBtn data-testid="signature-clear" onClick={clear} className="min-h-[40px] text-xs">Clear & redraw</GhostBtn>
      </div>
    </div>
  );
}

export default function SignOff({ shift, onBack, onSubmitted }) {
  const profile = getProfile();
  const [name, setName] = useState(profile?.name || "");
  const [handover, setHandover] = useState("");
  const [declared, setDeclared] = useState(false);
  const [signature, setSignature] = useState(null);
  const [closure, setClosure] = useState(shift.closure === "final" ? "final_closure" : "handed_over");
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const idemKey = useMemo(() => crypto.randomUUID(), []);

  const exceptions = shift.tasks.filter((t) => t.status === "cannot" || t.status === "na");
  const done = shift.tasks.filter((t) => t.status === "done");
  const pending = shift.tasks.filter((t) => t.status === "todo");

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/shifts/${shift.shift_id}/submit`, {
        signature, declaration: declared, confirmed_name: name,
        handover, closure_outcome: closure, idempotency_key: idemKey,
      });
      setReceipt(data);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  if (receipt) {
    return (
      <Shell title="Shift report sent">
        <Panel className="p-6 text-center space-y-3 border-[#ff2e4d]/50" data-testid="signoff-receipt">
          <p className="text-3xl uppercase italic" style={{ fontFamily: "Anton, sans-serif" }}>Signed off</p>
          <p className="text-white/60 text-sm">Report reference</p>
          <p data-testid="report-ref" className="text-xl font-mono tracking-wider text-[#ff2e4d]">{receipt.report_ref}</p>
          <p className="text-white/50 text-sm">
            Submitted and saved. Shift summary email to the Pedal Pop team: <span className="text-white/80">{receipt.email_status || "queued"}</span> — it sends and retries automatically.
          </p>
          <BigBtn data-testid="receipt-done-button" onClick={async () => {
            const { data } = await api.post("/shifts/open", { shift_type: shift.shift_type });
            onSubmitted(data);
          }}>Done</BigBtn>
        </Panel>
      </Shell>
    );
  }

  return (
    <Shell title="Review & sign" back={null}>
      <button data-testid="signoff-back" onClick={onBack} className="text-sm text-white/50 underline underline-offset-4 mb-4 min-h-[44px]">← Back to checklist</button>

      {pending.length > 0 && (
        <Panel className="p-4 mb-4 border-amber-400/40" data-testid="signoff-pending-warning">
          <p className="text-amber-400 font-semibold text-sm">{pending.length} task(s) still need an outcome</p>
          <ul className="text-sm text-white/55 mt-1 list-disc pl-5">
            {pending.slice(0, 6).map((t) => <li key={t.task_id}>{t.title}</li>)}
            {pending.length > 6 && <li>…and {pending.length - 6} more</li>}
          </ul>
          <p className="text-xs text-white/40 mt-2">Go back and mark each one Done, Cannot complete (with a reason) or Not applicable.</p>
        </Panel>
      )}

      {exceptions.length > 0 && (
        <section className="mb-5">
          <h3 className="text-xs uppercase tracking-[0.2em] text-amber-400 mb-2">Exceptions first ({exceptions.length})</h3>
          <div className="space-y-2">
            {exceptions.map((t) => (
              <Panel key={t.task_id} className="p-3.5 border-amber-400/30">
                <p className="text-sm">{t.title}</p>
                <p className="text-xs text-amber-400 mt-0.5">{t.status === "na" ? "Not applicable" : "Cannot complete"} — {t.reason}{t.note ? ` · ${t.note}` : ""}</p>
              </Panel>
            ))}
          </div>
        </section>
      )}

      <section className="mb-6">
        <h3 className="text-xs uppercase tracking-[0.2em] text-white/45 mb-2">Completed ({done.length})</h3>
        <Panel className="p-4 max-h-44 overflow-y-auto">
          <ul className="text-sm text-white/60 space-y-1.5">
            {done.map((t) => <li key={t.task_id}>✓ {t.title}{t.done_by ? ` — ${t.done_by}` : ""}</li>)}
          </ul>
        </Panel>
      </section>

      <div className="space-y-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-white/45 mb-2">Closure outcome</p>
          <div className="grid grid-cols-1 gap-2" data-testid="closure-options">
            {[
              { key: "handed_over", label: "Handed over — venue remains open", desc: "Next shift takes over. No lock-up required." },
              { key: "final_closure", label: "Final closure completed", desc: "Venue locked, air conditioning off where scheduled." },
            ].map((o) => (
              <button key={o.key} data-testid={`closure-${o.key}`} onClick={() => setClosure(o.key)}
                className={`rounded-xl border p-3.5 text-left min-h-[48px] transition ${closure === o.key ? "border-[#ff2e4d] bg-[#ff2e4d]/10" : "border-white/15 bg-[#141414]"}`}>
                <span className="block text-sm font-semibold">{o.label}</span>
                <span className="block text-xs text-white/45">{o.desc}</span>
              </button>
            ))}
          </div>
        </div>
        <textarea data-testid="handover-input" value={handover} onChange={(e) => setHandover(e.target.value)}
          placeholder="Optional handover note for the next shift…" rows={2} className={inputCls + " py-3 min-h-[60px]"} />
        <input data-testid="confirm-name-input" value={name} onChange={(e) => setName(e.target.value)}
          placeholder="Confirm your full name" className={inputCls} />
        <label className="flex items-start gap-3 min-h-[44px] cursor-pointer">
          <input data-testid="declaration-checkbox" type="checkbox" checked={declared} onChange={(e) => setDeclared(e.target.checked)}
            className="mt-1 h-5 w-5 accent-[#ff2e4d]" />
          <span className="text-sm text-white/65">I confirm this record accurately reflects the work completed and the exceptions noted on this shift.</span>
        </label>
        <SignaturePad onChange={setSignature} />
        <BigBtn data-testid="sign-send-button" onClick={submit}
          disabled={busy || pending.length > 0 || !declared || !signature || !name.trim()}>
          {busy ? "Submitting…" : "Sign & send shift report"}
        </BigBtn>
      </div>
    </Shell>
  );
}
